import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Link2, Unlink } from 'lucide-react';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';

const ExactConfigurationDoc = graphql(/* GraphQL */ `
  query ExactConfiguration {
    exactConfiguration {
      enabled
      credentialsConfigured
      status
      division
      scopeEnabled
      lastError
      mapping {
        journalCode
        entryDescription
        revenueLowGlId
        revenueLowGlCode
        revenueLowGlDescription
        vatCodeLow
        vatPercentageLow
        revenueHighGlId
        revenueHighGlCode
        revenueHighGlDescription
        vatCodeHigh
        vatPercentageHigh
        adyenGlId
        adyenGlCode
        adyenGlDescription
        cashGlId
        cashGlCode
        cashGlDescription
        tipsGlId
        tipsGlCode
        tipsGlDescription
        tabsGlId
        tabsGlCode
        tabsGlDescription
        salesJournalCode
        itemLowId
        itemLowCode
        itemLowDescription
        itemHighId
        itemHighCode
        itemHighDescription
        ticketGlId
        ticketGlCode
        ticketGlDescription
        ticketItemId
        ticketItemCode
        ticketItemDescription
        ticketVatCode
        ticketVatPercentage
        bookingFeeGlId
        bookingFeeGlCode
        bookingFeeGlDescription
        paypalGlId
        paypalGlCode
        paypalGlDescription
      }
    }
  }
`);

const ExactDivisionsDoc = graphql(/* GraphQL */ `
  query ExactDivisions {
    exactDivisions {
      code
      description
    }
  }
`);

const ExactGLAccountsDoc = graphql(/* GraphQL */ `
  query ExactGLAccounts {
    exactGLAccounts {
      id
      code
      description
    }
  }
`);

const ExactVATCodesDoc = graphql(/* GraphQL */ `
  query ExactVATCodes {
    exactVATCodes {
      id
      code
      description
      percentage
    }
  }
`);

const ExactJournalsDoc = graphql(/* GraphQL */ `
  query ExactJournals {
    exactJournals {
      code
      description
    }
  }
`);

const ExactItemsDoc = graphql(/* GraphQL */ `
  query ExactItems {
    exactItems {
      id
      code
      description
    }
  }
`);

const StartExactAuthorizationDoc = graphql(/* GraphQL */ `
  mutation StartExactAuthorization {
    startExactAuthorization
  }
`);

const DisconnectExactDoc = graphql(/* GraphQL */ `
  mutation DisconnectExact {
    disconnectExact {
      status
      division
      scopeEnabled
      lastError
    }
  }
`);

const SaveExactDivisionDoc = graphql(/* GraphQL */ `
  mutation SaveExactDivision($division: Int) {
    saveExactDivision(division: $division) {
      division
      scopeEnabled
    }
  }
`);

const SaveExactTicketMappingDoc = graphql(/* GraphQL */ `
  mutation SaveExactTicketMapping($input: SaveExactTicketMappingInput!) {
    saveExactTicketMapping(input: $input) {
      ticketGlId
      ticketGlCode
      ticketItemId
      ticketItemCode
      ticketVatCode
      ticketVatPercentage
      bookingFeeGlId
      bookingFeeGlCode
      paypalGlId
      paypalGlCode
    }
  }
`);

const SaveExactMappingDoc = graphql(/* GraphQL */ `
  mutation SaveExactMapping($input: SaveExactMappingInput!) {
    saveExactMapping(input: $input) {
      mapping {
        journalCode
        entryDescription
        revenueLowGlId
        revenueLowGlCode
        vatCodeLow
        vatPercentageLow
        revenueHighGlId
        revenueHighGlCode
        vatCodeHigh
        vatPercentageHigh
        adyenGlId
        adyenGlCode
        cashGlId
        cashGlCode
        tipsGlId
        tipsGlCode
        tabsGlId
        tabsGlCode
        salesJournalCode
        itemLowId
        itemLowCode
        itemHighId
        itemHighCode
      }
    }
  }
`);

/**
 * Exact Online config for the active scope: pick a division (or disable), then
 * map ledger accounts when enabled.
 */
export function ExactOnlineTab() {
  const { data, previousData, loading, refetch } = useQuery(ExactConfigurationDoc);
  const cfg = (data ?? previousData)?.exactConfiguration;
  const connected = cfg?.status === 'connected';
  const scopeEnabled = Boolean(cfg?.scopeEnabled);

  const [startAuth, { loading: startingAuth, error: authError }] =
    useMutation(StartExactAuthorizationDoc);
  const [disconnect, { loading: disconnecting }] = useMutation(DisconnectExactDoc);
  const [saveDivision, { loading: savingDivision, error: divisionError }] =
    useMutation(SaveExactDivisionDoc);

  const divisionsQuery = useQuery(ExactDivisionsDoc, { skip: !connected });
  const glQuery = useQuery(ExactGLAccountsDoc, { skip: !connected || !scopeEnabled });
  const vatQuery = useQuery(ExactVATCodesDoc, { skip: !connected || !scopeEnabled });
  const journalQuery = useQuery(ExactJournalsDoc, { skip: !connected || !scopeEnabled });
  const itemsQuery = useQuery(ExactItemsDoc, { skip: !connected || !scopeEnabled });

  const divisionOptions = useMemo(
    () => [
      { value: '', label: 'Disabled for this scope' },
      ...(divisionsQuery.data?.exactDivisions ?? []).map((d) => ({
        value: String(d.code),
        label: `${d.code} — ${d.description}`,
      })),
    ],
    [divisionsQuery.data],
  );

  const glOptions = useMemo(
    () =>
      (glQuery.data?.exactGLAccounts ?? []).map((a) => ({
        value: a.id,
        label: `${a.code} — ${a.description}`,
        code: a.code,
        description: a.description,
      })),
    [glQuery.data],
  );
  const vatOptions = useMemo(
    () =>
      (vatQuery.data?.exactVATCodes ?? []).map((a) => ({
        value: a.code,
        label: `${a.code} — ${a.description} (${a.percentage}%)`,
        percentage: a.percentage,
      })),
    [vatQuery.data],
  );
  const journalOptions = useMemo(
    () =>
      (journalQuery.data?.exactJournals ?? []).map((j) => ({
        value: j.code,
        label: `${j.code} — ${j.description}`,
      })),
    [journalQuery.data],
  );
  const itemOptions = useMemo(
    () =>
      (itemsQuery.data?.exactItems ?? []).map((a) => ({
        value: a.id,
        label: `${a.code} — ${a.description}`,
        code: a.code,
        description: a.description,
      })),
    [itemsQuery.data],
  );

  const mapping = cfg?.mapping;
  const [journalCode, setJournalCode] = useState('90');
  const [entryDescription, setEntryDescription] = useState('Kassa');
  const [revenueLowGlId, setRevenueLowGlId] = useState('');
  const [vatCodeLow, setVatCodeLow] = useState('');
  const [vatPercentageLow, setVatPercentageLow] = useState(9);
  const [revenueHighGlId, setRevenueHighGlId] = useState('');
  const [vatCodeHigh, setVatCodeHigh] = useState('');
  const [vatPercentageHigh, setVatPercentageHigh] = useState(21);
  const [adyenGlId, setAdyenGlId] = useState('');
  const [cashGlId, setCashGlId] = useState('');
  const [tipsGlId, setTipsGlId] = useState('');
  const [tabsGlId, setTabsGlId] = useState('');
  const [salesJournalCode, setSalesJournalCode] = useState('70');
  const [itemLowId, setItemLowId] = useState('');
  const [itemHighId, setItemHighId] = useState('');
  const [ticketGlId, setTicketGlId] = useState('');
  const [ticketItemId, setTicketItemId] = useState('');
  const [ticketVatCode, setTicketVatCode] = useState('');
  const [ticketVatPercentage, setTicketVatPercentage] = useState(21);
  const [bookingFeeGlId, setBookingFeeGlId] = useState('');
  const [paypalGlId, setPaypalGlId] = useState('');
  const [mapSeeded, setMapSeeded] = useState(false);
  if (mapping && !mapSeeded) {
    setMapSeeded(true);
    setJournalCode(mapping.journalCode || '90');
    setEntryDescription(mapping.entryDescription || 'Kassa');
    setRevenueLowGlId(mapping.revenueLowGlId ?? '');
    setVatCodeLow(mapping.vatCodeLow ?? '');
    setVatPercentageLow(mapping.vatPercentageLow ?? 9);
    setRevenueHighGlId(mapping.revenueHighGlId ?? '');
    setVatCodeHigh(mapping.vatCodeHigh ?? '');
    setVatPercentageHigh(mapping.vatPercentageHigh ?? 21);
    setAdyenGlId(mapping.adyenGlId ?? '');
    setCashGlId(mapping.cashGlId ?? '');
    setTipsGlId(mapping.tipsGlId ?? '');
    setTabsGlId(mapping.tabsGlId ?? '');
    setSalesJournalCode(mapping.salesJournalCode || '70');
    setItemLowId(mapping.itemLowId ?? '');
    setItemHighId(mapping.itemHighId ?? '');
    setTicketGlId(mapping.ticketGlId ?? '');
    setTicketItemId(mapping.ticketItemId ?? '');
    setTicketVatCode(mapping.ticketVatCode ?? '');
    setTicketVatPercentage(mapping.ticketVatPercentage ?? 21);
    setBookingFeeGlId(mapping.bookingFeeGlId ?? '');
    setPaypalGlId(mapping.paypalGlId ?? '');
  }

  const [saveMapping, { loading: savingMap, error: mapError }] =
    useMutation(SaveExactMappingDoc);
  const [saveTicketMapping, { loading: savingTicketMap, error: ticketMapError }] =
    useMutation(SaveExactTicketMappingDoc);

  if (loading && !cfg) {
    return <p className="text-sm text-gray-500">Loading Exact Online configuration…</p>;
  }

  if (cfg && !cfg.enabled) {
    return (
      <div
        id="admin.exact.unavailable"
        className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100"
      >
        Exact Online is not available in this environment.
      </div>
    );
  }

  const handleConnect = async () => {
    const result = await startAuth();
    const url = result.data?.startExactAuthorization;
    if (url) {
      window.location.assign(url);
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    setMapSeeded(false);
    await refetch();
  };

  const handleDivisionChange = async (value: string) => {
    const division = value === '' ? null : Number(value);
    await saveDivision({ variables: { division } });
    setMapSeeded(false);
    await refetch();
  };

  const glMeta = (id: string) => glOptions.find((o) => o.value === id);
  const itemMeta = (id: string) => itemOptions.find((o) => o.value === id);

  const handleSaveMapping = async (event: React.FormEvent) => {
    event.preventDefault();
    const low = glMeta(revenueLowGlId);
    const high = glMeta(revenueHighGlId);
    const adyen = glMeta(adyenGlId);
    const cash = glMeta(cashGlId);
    const tips = glMeta(tipsGlId);
    const tabs = glMeta(tabsGlId);
    const itemLow = itemMeta(itemLowId);
    const itemHigh = itemMeta(itemHighId);
    if (
      !low ||
      !high ||
      !adyen ||
      !cash ||
      !tips ||
      !tabs ||
      !vatCodeLow ||
      !vatCodeHigh ||
      !itemLow ||
      !itemHigh ||
      !salesJournalCode
    ) {
      return;
    }
    await saveMapping({
      variables: {
        input: {
          journalCode,
          entryDescription,
          revenueLowGlId,
          revenueLowGlCode: low.code,
          revenueLowGlDescription: low.description,
          vatCodeLow,
          vatPercentageLow,
          revenueHighGlId,
          revenueHighGlCode: high.code,
          revenueHighGlDescription: high.description,
          vatCodeHigh,
          vatPercentageHigh,
          adyenGlId,
          adyenGlCode: adyen.code,
          adyenGlDescription: adyen.description,
          cashGlId,
          cashGlCode: cash.code,
          cashGlDescription: cash.description,
          tipsGlId,
          tipsGlCode: tips.code,
          tipsGlDescription: tips.description,
          tabsGlId,
          tabsGlCode: tabs.code,
          tabsGlDescription: tabs.description,
          salesJournalCode,
          itemLowId,
          itemLowCode: itemLow.code,
          itemLowDescription: itemLow.description,
          itemHighId,
          itemHighCode: itemHigh.code,
          itemHighDescription: itemHigh.description,
        },
      },
    });
    await refetch();
  };

  if (!connected) {
    return (
      <FormLayout
        title="Division"
        description="Connect Exact Online to choose an administration for this scope."
        onSubmit={(event) => event.preventDefault()}
        footer={
          <div className="flex flex-wrap items-center gap-3">
            <Button
              id="admin.exact.connect"
              type="button"
              variant="secondary"
              leftIcon={<Link2 className="h-4 w-4" />}
              loading={startingAuth}
              disabled={!cfg?.credentialsConfigured}
              onClick={() => void handleConnect()}
            >
              Connect to Exact Online
            </Button>
            <Badge id="admin.exact.status" tone="neutral">
              Disconnected
            </Badge>
            {authError && <span className="text-sm text-red-600">{authError.message}</span>}
            {cfg?.lastError && (
              <span className="text-sm text-red-600">{cfg.lastError}</span>
            )}
          </div>
        }
      >
        <FormSection title="Exact Online">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            After connecting, select a division to enable bookings for this scope.
          </p>
        </FormSection>
      </FormLayout>
    );
  }

  return (
    <div className="space-y-8">
      <FormLayout
        title="Division"
        description="Select an Exact administration for this scope, or disable Exact export here."
        onSubmit={(event) => event.preventDefault()}
        footer={
          <div className="flex flex-wrap items-center gap-3">
            <Button
              id="admin.exact.disconnect"
              type="button"
              variant="outline"
              leftIcon={<Unlink className="h-4 w-4" />}
              loading={disconnecting}
              onClick={() => void handleDisconnect()}
            >
              Disconnect
            </Button>
            <Badge id="admin.exact.status" tone="success">
              Connected
            </Badge>
            {cfg?.lastError && (
              <span className="text-sm text-red-600">{cfg.lastError}</span>
            )}
          </div>
        }
      >
        <FormSection title="Administratie">
          <FormField label="Division">
            <Select
              id="admin.exact.division"
              value={cfg?.division != null ? String(cfg.division) : ''}
              onChange={(e) => void handleDivisionChange(e.target.value)}
              disabled={savingDivision || divisionsQuery.loading}
              options={
                divisionOptions.length > 1
                  ? divisionOptions
                  : [
                      { value: '', label: 'Disabled for this scope' },
                      ...(cfg?.division != null
                        ? [{ value: String(cfg.division), label: String(cfg.division) }]
                        : []),
                    ]
              }
            />
          </FormField>
          {(divisionError || divisionsQuery.error) && (
            <p className="text-sm text-red-600">
              {(divisionError ?? divisionsQuery.error)?.message}
            </p>
          )}
        </FormSection>
      </FormLayout>

      {scopeEnabled && (
        <FormLayout
          title="Ledger mapping"
          description="Map Ferry tax-report buckets to Exact grootboekrekeningen and BTW-codes."
          onSubmit={handleSaveMapping}
          footer={
            <div className="flex flex-wrap items-center gap-3">
              <Button id="admin.exact.mapping.save" type="submit" loading={savingMap}>
                Save mapping
              </Button>
              {mapError && (
                <span id="admin.exact.mapping.error" className="text-sm text-red-600">
                  {mapError.message}
                </span>
              )}
            </div>
          }
        >
          <FormSection title="Journal" description="Memoriaal dagboek for the daily kassa booking.">
            {(glQuery.error || vatQuery.error || journalQuery.error) && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Could not load Exact pick-lists
                {glQuery.error ? `: ${glQuery.error.message}` : ''}
                {!glQuery.error && vatQuery.error ? `: ${vatQuery.error.message}` : ''}
                {!glQuery.error && !vatQuery.error && journalQuery.error
                  ? `: ${journalQuery.error.message}`
                  : ''}
              </p>
            )}
            <FormField label="Dagboek">
              <Select
                id="admin.exact.mapping.journal"
                value={journalCode}
                onChange={(e) => setJournalCode(e.target.value)}
                options={
                  journalOptions.length > 0
                    ? journalOptions
                    : [{ value: journalCode || '90', label: journalCode || '90' }]
                }
              />
            </FormField>
            <FormField label="Omschrijving">
              <Input
                value={entryDescription}
                onChange={(e) => setEntryDescription(e.target.value)}
              />
            </FormField>
          </FormSection>

          <FormSection title="Omzet & BTW" description="Revenue GL accounts and Exact VAT codes (excluding tax).">
            <FormField label="Grootboekrekening omzet laag tarief">
              <Select
                id="admin.exact.mapping.revenueLow"
                value={revenueLowGlId}
                onChange={(e) => setRevenueLowGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField label="BTW-code laag">
              <Select
                id="admin.exact.mapping.vatLow"
                value={vatCodeLow}
                onChange={(e) => {
                  const code = e.target.value;
                  setVatCodeLow(code);
                  const opt = vatOptions.find((o) => o.value === code);
                  if (opt) setVatPercentageLow(opt.percentage);
                }}
                options={[{ value: '', label: 'Select…' }, ...vatOptions]}
              />
            </FormField>
            <FormField label="Grootboekrekening omzet hoog tarief">
              <Select
                id="admin.exact.mapping.revenueHigh"
                value={revenueHighGlId}
                onChange={(e) => setRevenueHighGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField label="BTW-code hoog">
              <Select
                id="admin.exact.mapping.vatHigh"
                value={vatCodeHigh}
                onChange={(e) => {
                  const code = e.target.value;
                  setVatCodeHigh(code);
                  const opt = vatOptions.find((o) => o.value === code);
                  if (opt) setVatPercentageHigh(opt.percentage);
                }}
                options={[{ value: '', label: 'Select…' }, ...vatOptions]}
              />
            </FormField>
          </FormSection>

          <FormSection title="Payment accounts" description="Pin (Adyen), cash, tips (Fooien), and the tabs clearing account.">
            <FormField label="Grootboekrekening Adyen">
              <Select
                id="admin.exact.mapping.adyen"
                value={adyenGlId}
                onChange={(e) => setAdyenGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField label="Grootboekrekening Kas">
              <Select
                id="admin.exact.mapping.cash"
                value={cashGlId}
                onChange={(e) => setCashGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField label="Grootboekrekening Fooien / Tips">
              <Select
                id="admin.exact.mapping.tips"
                value={tipsGlId}
                onChange={(e) => setTipsGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField
              label="Grootboekrekening Nog te ontvangen / tabs"
              description="Tussenrekening voor omzet die op een tab is geboekt maar (nog) niet is betaald."
            >
              <Select
                id="admin.exact.mapping.tabs"
                value={tabsGlId}
                onChange={(e) => setTabsGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
          </FormSection>

          <FormSection
            title="Sales invoices"
            description="Open Exact SalesInvoices for invoice tabs at cutoff (editable until printed)."
          >
            {(itemsQuery.error) && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Could not load Exact items: {itemsQuery.error.message}
              </p>
            )}
            <FormField label="Verkoopdagboek">
              <Select
                id="admin.exact.mapping.salesJournal"
                value={salesJournalCode}
                onChange={(e) => setSalesJournalCode(e.target.value)}
                options={
                  journalOptions.length > 0
                    ? journalOptions
                    : [{ value: salesJournalCode || '70', label: salesJournalCode || '70' }]
                }
              />
            </FormField>
            <FormField label="Exact artikel laag BTW">
              <Select
                id="admin.exact.mapping.itemLow"
                value={itemLowId}
                onChange={(e) => setItemLowId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...itemOptions]}
              />
            </FormField>
            <FormField label="Exact artikel hoog BTW">
              <Select
                id="admin.exact.mapping.itemHigh"
                value={itemHighId}
                onChange={(e) => setItemHighId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...itemOptions]}
              />
            </FormField>
          </FormSection>
        </FormLayout>
      )}

      {scopeEnabled && (
        <FormLayout
          title="Tickets & PayPal"
          description="Ticket revenue GL / Item / VAT (admission and booking fee) and the PayPal payment bucket. Ticket sales cannot ship in this scope until both this mapping and the Tickets tab tax rate are set. Online Adyen reuses the existing Adyen GL."
          onSubmit={async (event) => {
            event.preventDefault();
            const ticketGl = glMeta(ticketGlId);
            const ticketItem = itemMeta(ticketItemId);
            if (!ticketGl || !ticketItem || !ticketVatCode) return;
            const feeGl = bookingFeeGlId ? glMeta(bookingFeeGlId) : undefined;
            const paypal = paypalGlId ? glMeta(paypalGlId) : undefined;
            await saveTicketMapping({
              variables: {
                input: {
                  ticketGlId,
                  ticketGlCode: ticketGl.code,
                  ticketGlDescription: ticketGl.description,
                  ticketItemId,
                  ticketItemCode: ticketItem.code,
                  ticketItemDescription: ticketItem.description,
                  ticketVatCode,
                  ticketVatPercentage,
                  bookingFeeGlId: feeGl?.value ?? null,
                  bookingFeeGlCode: feeGl?.code ?? null,
                  bookingFeeGlDescription: feeGl?.description ?? null,
                  paypalGlId: paypal?.value ?? null,
                  paypalGlCode: paypal?.code ?? null,
                  paypalGlDescription: paypal?.description ?? null,
                },
              },
            });
            await refetch();
          }}
          footer={
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" loading={savingTicketMap}>
                Save ticket & PayPal mapping
              </Button>
              {ticketMapError && (
                <span className="text-sm text-red-600">{ticketMapError.message}</span>
              )}
            </div>
          }
        >
          <FormSection
            title="Ticket revenue"
            description="Admission GL, Exact item, and VAT code. Applied to ticket order items at checkout."
          >
            <FormField label="Grootboekrekening tickets (admission)">
              <Select
                value={ticketGlId}
                onChange={(e) => setTicketGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
            <FormField label="Exact artikel tickets">
              <Select
                value={ticketItemId}
                onChange={(e) => setTicketItemId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...itemOptions]}
              />
            </FormField>
            <FormField label="BTW-code tickets">
              <Select
                value={ticketVatCode}
                onChange={(e) => {
                  const code = e.target.value;
                  setTicketVatCode(code);
                  const opt = vatOptions.find((o) => o.value === code);
                  if (opt) setTicketVatPercentage(opt.percentage);
                }}
                options={[{ value: '', label: 'Select…' }, ...vatOptions]}
              />
            </FormField>
            <FormField
              label="Grootboekrekening booking fee (optional)"
              description="When unset the fee books to the ticket GL."
            >
              <Select
                value={bookingFeeGlId}
                onChange={(e) => setBookingFeeGlId(e.target.value)}
                options={[{ value: '', label: 'Same as admission' }, ...glOptions]}
              />
            </FormField>
          </FormSection>
          <FormSection
            title="PayPal"
            description="Required before PayPal checkout ships — unmapped PSP labels are not booked."
          >
            <FormField label="Grootboekrekening PayPal">
              <Select
                value={paypalGlId}
                onChange={(e) => setPaypalGlId(e.target.value)}
                options={[{ value: '', label: 'Select…' }, ...glOptions]}
              />
            </FormField>
          </FormSection>
        </FormLayout>
      )}
    </div>
  );
}
